import type { Adjustment, MaterialInfo, PartDefinition } from '@rogue/rackbuilder'
import type { RackOptions } from '../../../RackTypes'
import { AdjustOnlyVisual } from '../../../constants'
import { getRackModel, MONSTER_SPOTTER_ARMS, UTILITY_BENCH, ADJ_BENCH_3 } from '../../../modelPaths'
import { BarBuilder } from '../../bar/barBuilder'

/**
 * Handles creation of all rack accessories
 * (safeties, j-cups, spotter arms, band pegs, bench, bar)
 */
export class AccessoryBuilder {
  constructor(
    private config: RackOptions,
    private material: MaterialInfo
  ) {}
  
  createSafety(left: boolean): PartDefinition {
    const safetyAdjustments = this.getSafetyAdjustments(left)
    
    return {
      mesh           : getRackModel(`${this.config.line}_SAFETY`, `${this.config.safeties}-${this.config.depth}`),
      slots          : {},
      adjustments    : safetyAdjustments,
      adjustmentType : AdjustOnlyVisual,
      materials      : this.config.materials?.safeties || { main: { color: 'black', material: 'mg' } },
    }
  }
  
  private getSafetyAdjustments(left: boolean): Adjustment[] {
    if (!left && this.config.safeties === 'strap-safety') {
      return [{ type: 'flip', axis: 'Z' }, { type: 'rotate', axis: 'Y', amount: 180 }]
    }
    if (left && this.config.safeties === 'flipdown-safety') {
      return [{ type: 'flip', axis: 'Z' }, { type: 'rotate', axis: 'Y', amount: 180 }]
    }
    
    return []
  }
  
  createJCup(left: boolean): PartDefinition {
    const ghostJcupMaterials = this.getGhostJcupMaterials(left)
    
    return {
      mesh        : getRackModel(`${this.config.line}_JCUPS`, this.config.jcup!),
      slots       : {},
      materials   : this.config.jcup === 'ghost-jcup' ? ghostJcupMaterials : {},
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Z', amount: 180 }, { type: 'flip', axis: 'Y' }],
    }
  }
  
  private getGhostJcupMaterials(left: boolean): MaterialInfo {
    const rightSticker = { 
      sticker: { 
        color  : '#ffffff', 
        config : { maps: { map: 'https://assets.roguefitness.com/w_512,q_auto,f_auto/glb/txt/jcups/ghost_right.jpg' } }, 
      }, 
    }
    
    if (this.config.line === 'RM' && left) {
      return { pin: { material: 'stainless' } }
    }
    if (this.config.line === 'RM' && !left) {
      return { pin: { material: 'stainless' }, ...rightSticker }
    }
    if (!left) {
      return rightSticker
    }
    
    return {}
  }
  
  createBar(): PartDefinition {
    // Add plates if available
    if ( this.config?.bar && this.config?.plates) {
      this.config.bar.plates = this.config.plates
    }
    
    return new BarBuilder(this.config.bar!).build()
  }
  
  createSpotterArms(left: boolean): PartDefinition {
    if (this.config.line === 'RML') {
      return {
        mesh        : getRackModel('RML_SAFETY_SPOTTER_ARMS', 'safety spotter arms'),
        slots       : {},
        adjustments : left ? [] : [{ axis: 'Z', type: 'rotate', amount: 180, pieces: ['clasp'] }],
        offset      : { y: 2, x: 0, z: 0 },
      }
    }
    
    // RM spotter arms with branding plate
    const brandingPlate = left ? 'left branding plate' : 'right branding plate'
    
    return {
      mesh  : getRackModel('RM_SAFETY_SPOTTER_ARMS', 'spotter arms'),
      slots : {
        middle: {
          mesh   : `${MONSTER_SPOTTER_ARMS}#${brandingPlate}`,
          slots  : {},
          anchor : 'middle',
        },
      },
      adjustments: left ? [] : [{ axis: 'Z', type: 'rotate', amount: 180, pieces: ['clasp'] }],
    }
  }
  
  addBandPegs(frontUpright: PartDefinition, backUpright: PartDefinition, left: boolean): void {
    const bottomCrossmember = (frontUpright.slots['crossmember-bottom'] as PartDefinition[])[0]
    
    const frontPeg = this.createBandPeg(left, { z: -2 })
    const backPeg = this.createBandPeg(left, { z: 2 })
    
    bottomCrossmember.slots.first = frontPeg
    bottomCrossmember.slots.last = backPeg
  }
  
  private createBandPeg(left: boolean, offset: { z: number }): PartDefinition {
    return {
      mesh        : getRackModel(`${this.config.line}_STORAGE`, 'band_peg'),
      slots       : {},
      anchor      : 'front',
      materials   : this.config.bandPegs === 'zinc' ? { main: { material: 'zinc' } } : {},
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Z', amount: 180 }],
      offset,
    }
  }
  
  createBench(): PartDefinition {
    const offset = this.config.backStabilizer ? { z: 65 } : { z: 45 }
    const mesh = this.config.bench === 'flat' ? UTILITY_BENCH : ADJ_BENCH_3
    
    return {
      mesh,
      slots: {},
      offset,
    }
  }
}

